import * as THREE from "three";
import { rand } from "../color.ts";
import { makePrims, type Prims } from "../meshkit.ts";

/** The prim set a deco build may use — the shared meshkit `Prims` (one
 *  signature source of truth: box/blob/cone/cylinder/tube/rod plus the rounded
 *  ball/arc/bone/lathe, plan 28 stage 4) minus the rig-only limb/socket/inLimb
 *  (decos aren't jointed). Both build paths honor every member identically:
 *  buildDeco below spreads the real makePrims, and the instanced baker
 *  (src/instanced.ts bakeDeco) bakes through the same makePrims. */
export type DecoBuildCtx = Pick<
  Prims,
  "box" | "blob" | "cone" | "cylinder" | "tube" | "rod" | "ball" | "arc" | "bone" | "lathe"
> & {
  /** Deterministic per-instance seed (from world position) for variation. */
  seed: number;
};

/** Where a deco is allowed to spawn during scatter. */
export type Habitat = "land" | "shore" | "water";

/** Player interaction offered by a deco — presence = interactable (click-to-focus
 *  + the verb in action-bar slot 1), mirroring the combat/talk/weapon
 *  block-presence-is-the-switch pattern. Authored in content/decos/<type>.json
 *  (InteractSchema); consumed by src/interact.ts (client) and the `interact`
 *  handler in index.ts (server — open/shake/pick mutate shared state there).
 *  Kinds:
 *   open  — one-time chest: server spawns `loot` on the ground, model swaps to
 *           its open build ("<model>-open" coded entry), state persists
 *   shake — rolls `dropChance` for a `dropItem` ground drop; a SUCCESS arms a
 *           per-instance `cooldown` (seconds)
 *   pick  — adds `item` to the picker's bag; the instance hides for `respawn`
 *           seconds (everyone sees it gone), then reappears
 *   read  — client-only: shows the sign plaque overlay, no server round-trip
 *   use   — client-only STATION verb: focusing + triggering fires a local
 *           activity keyed by the deco type (the kitchen range's Cook → cook
 *           mode, plan 25). No server round-trip — the activity's own intent
 *           (`cook`) is what the server validates, so a forged `interact` on a
 *           use-prop falls through the server's kind chain harmlessly.
 *   chop  — tree-cutting: requires a hatchet on the held-tool channel; starts
 *           a server-locked minigame session; success grants `logs`×`logCount`
 *           and marks the prop cut (stump) until `respawn` seconds.
 */
export type InteractSpec = {
  name: string;
  verb: string;
  kind: "open" | "shake" | "pick" | "read" | "use" | "chop";
  /** Reach (xz units) on top of the model's radius·scale. Default 2.2. */
  range?: number;
  dropItem?: string;
  dropChance?: number;
  cooldown?: number;
  item?: string;
  respawn?: number;
  /** chop: log item type granted on a successful cut. */
  logs?: string;
  /** chop: units of logs granted. Default 1 (raise for larger trees). */
  logCount?: number;
  loot?: { type: string; count?: number }[];
};

/** How close a player must stand to interact: the block's range on top of the
 *  instance's visual footprint (radius·scale), so a wide tree is reachable from
 *  outside its canopy. THE shared reach formula — the client gate (interact.ts)
 *  and the server validation (index.ts, which adds desync slack on top) both
 *  call this, so tuning the default can't desync them. */
export function interactReach(it: InteractSpec, radius: number, scale: number): number {
  return (it.range ?? 2.2) + radius * scale;
}

export type DecoSpec = {
  type: string;
  /** Random-spawn weight. 0 = placed landmark, never scattered. */
  weight: number;
  mapColor: number;
  radius: number;
  /** Blocks player movement (collision). Off by default — small ground cover
   *  (grass, flowers…) stays walk-through. The collider radius is `radius`. */
  solid?: boolean;
  /** Collider height (pre-scale — gravity plan): bounds the solid vertically so
   *  an arc can clear/land on it. Omitted = full-height (trees). */
  height?: number;
  /** With `height`: the top is standable — world.ts registers a prop platform. */
  standable?: boolean;
  /** #4 Sway amount in radians (0 = static). Animated in the render loop. */
  sway?: number;
  /** Terrain zone this model spawns in. Defaults to "land". */
  habitat?: Habitat;
  /** Presence = interactable (see InteractSpec above). */
  interact?: InteractSpec;
  /** The model id `build` was bound from (kept by load.ts) — an "open" interact
   *  resolves its opened build as `"<model>-open"` (src/interact.ts). */
  model?: string;
  build: (ctx: DecoBuildCtx) => void;
  /** Dev-studio only (src/studio): optional per-frame animation for the model
   *  viewer, receiving the built+normalized group. Ignored by the game and the
   *  headless server. Decos with `sway` already wobble in the studio by default;
   *  set this only for bespoke motion. */
  studioAnimate?: (o: THREE.Object3D, dt: number, t: number) => void;
};

export function defineDeco(spec: DecoSpec): DecoSpec {
  return spec;
}

// Models are authored from the shared mesh kit (see ../meshkit.ts) so decos and
// structures draw boxes/cones/blobs the same way. The faceted vs. smooth
// distinction lives in the GEOMETRY (cones/cylinders/tubes are run through
// flatten() for crisp facets; rounded boxes keep their smooth normals).

/** Run a deco's build() and return a Group positioned at the world spot. */
export function buildDeco(spec: DecoSpec, seed: number): THREE.Group {
  const group = new THREE.Group();
  const push = (mesh: THREE.Mesh) => {
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  };

  const ctx: DecoBuildCtx = { seed, ...makePrims(push, seed) };

  spec.build(ctx);

  if (spec.sway) {
    group.userData.swayAmount = spec.sway;
    group.userData.swayPhase = rand(seed) * Math.PI * 2;
  }
  return group;
}
